import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * How a loans list presents itself in the room it has:
 *   wide    — every column on one line
 *   compact — related columns folded into two-line cells
 *   stacked — one card per loan
 */
export type LoanListStep = 'wide' | 'compact' | 'stacked';

export interface LoanListLayout {
  table: LoanListStep;
  hybrid: LoanListStep;
  segmentColumns: 6 | 3 | 2;
  pipelineColumns: 6 | 3 | 2 | 1;
  cardColumns: 2 | 1;
}

// Thresholds are widths of the Loan Center content area, not the screen: the
// sidebar and page padding take a different share at each screen size. Each one
// is the smallest width at which the wider arrangement still fits without
// scrolling sideways.
const TABLE_WIDE_MIN = 1500; // thirteen single-line columns
const LIST_COMPACT_MIN = 880; // two-line columns; six summary cards at 140px each
const HYBRID_WIDE_MIN = 1380; // 260px stage rail beside eight columns, Borrower still readable
const PIPELINE_SIX_MIN = 1370; // six 220px stages plus gaps
const PIPELINE_THREE_MIN = 680;
const PIPELINE_TWO_MIN = 450;
const SEGMENT_THREE_MIN = 440;
const CARD_TWO_MIN = 600;

const WIDE_LAYOUT: LoanListLayout = {
  table: 'wide',
  hybrid: 'wide',
  segmentColumns: 6,
  pipelineColumns: 6,
  cardColumns: 2
};

function stepFor(width: number, wideMin: number): LoanListStep {
  if (width >= wideMin) {
    return 'wide';
  }
  return width >= LIST_COMPACT_MIN ? 'compact' : 'stacked';
}

export function loanListLayoutForWidth(width: number): LoanListLayout {
  // Zero or NaN means the container has not been measured yet (first render,
  // hidden ancestor, non-layout test environments).
  if (!(width > 0)) {
    return WIDE_LAYOUT;
  }
  return {
    table: stepFor(width, TABLE_WIDE_MIN),
    hybrid: stepFor(width, HYBRID_WIDE_MIN),
    segmentColumns: width >= LIST_COMPACT_MIN ? 6 : width >= SEGMENT_THREE_MIN ? 3 : 2,
    pipelineColumns:
      width >= PIPELINE_SIX_MIN ? 6
        : width >= PIPELINE_THREE_MIN ? 3
          : width >= PIPELINE_TWO_MIN ? 2
            : 1,
    cardColumns: width >= CARD_TWO_MIN ? 2 : 1
  };
}

function sameLayout(a: LoanListLayout, b: LoanListLayout): boolean {
  return a.table === b.table
    && a.hybrid === b.hybrid
    && a.segmentColumns === b.segmentColumns
    && a.pipelineColumns === b.pipelineColumns
    && a.cardColumns === b.cardColumns;
}

/**
 * Tracks the width of the element the returned ref is attached to and maps it
 * to a layout. State only changes when a threshold is crossed, so dragging a
 * window edge does not re-render the list on every frame.
 *
 * The steps rely on a narrower arrangement never being shorter than the wider
 * one. A page scrollbar that appears when a step narrows therefore stays, and
 * the measured width cannot flip back and forth across a threshold.
 */
export function useLoanListLayout<T extends HTMLElement>(): {
  ref: RefObject<T>;
  layout: LoanListLayout;
} {
  const ref = useRef<T>(null);
  const [layout, setLayout] = useState<LoanListLayout>(WIDE_LAYOUT);
  const current = useRef<LoanListLayout>(WIDE_LAYOUT);

  // Layout effect: the first measurement lands before the browser paints, so
  // the wide arrangement never flashes on a narrow screen.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const measure = () => {
      const width = element.clientWidth;
      // No width means hidden, not narrow: keep the last real measurement.
      if (!(width > 0)) {
        return;
      }
      const next = loanListLayoutForWidth(width);
      if (!sameLayout(current.current, next)) {
        current.current = next;
        setLayout(next);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, layout };
}
